/**
 * frontend/src/pages/valuation/_FinalizeCard.tsx
 *
 * VALUATION-B3 sign-off — the Finalize card and the wording it shares with the
 * sticky Next step bar.
 *
 * Split out of _SignOffGates.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'

export function FinalizeCard({
  methodologyReason,
  rationale,
  onRationaleChange,
  canFinalize,
  blockedReason,
  finalizing,
  onFinalize,
  liveVersion,
  willSign,
  reissueChanged,
  changedFieldLabels,
  delivered = false,
  tuned = false,
}: {
  /** The methodology is tuned for this engagement: only then is a Methodology reason required. */
  tuned?: boolean
  /** True once the live version is signed AND its document is on file: the card is
   *  then the amendment form behind "Start a new record" (no version numbers). */
  delivered?: boolean
  /** The reason typed on Methodology, shown read-only above the sign-off box. */
  methodologyReason: string
  rationale: string
  onRationaleChange: (value: string) => void
  canFinalize: boolean
  blockedReason: string | null
  finalizing: boolean
  onFinalize: () => void
  liveVersion: number | null
  /** True when this actor's finalize call both records AND signs (a
   *  credentialed role). False means it records only — VAL-SIGNOFF-GATE-1:
   *  an owner may record a calculation but not sign or print it. */
  willSign: boolean
  /**
   * VAL-REISSUE-GUARD-1 R3. `null` when this is a first finalize
   * (`liveVersion === null`) — the guard only applies to a re-finalize, and
   * a first finalize always writes v1. Otherwise the caller's client-side
   * mirror of `reissue_delta()`: `false` means nothing that can move the
   * number has changed since the live version.
   */
  reissueChanged: boolean | null
  /** Human labels for what changed, e.g. `['normalized earnings', '3 driver
   *  answers']` — only meaningful when `reissueChanged` is `true`. */
  changedFieldLabels: string[]
}) {
  // The server's own check in finalize.py is authoritative — this is a
  // convenience so the valuator sees "nothing to re-issue" before pressing
  // the button, not instead of the 409 the server still guards with.
  const noChangeMessage = noChangeText(liveVersion, reissueChanged)
  const changedSummary = liveVersion !== null && reissueChanged === true && changedFieldLabels.length > 0
    ? `Changed since the last record: ${changedFieldLabels.join(', ')}.`
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {liveVersion === null
            ? (willSign ? 'Finalize' : 'Record')
            : delivered ? 'New record' : 'Sign again (the signed record stays on file)'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* WALKFIX-C F4: plain words, owner approved WF-C-1..3 (OWNER_APPROVALS.log 2026-10-25). No version numbers (AMEND_1). */}
        <p className="text-xs text-gray-500">
          {liveVersion === null
            ? (willSign
              ? 'Finalizing saves the figures, the method and the value as a record that cannot change, and signs it as you. Nothing above is saved until you do.'
              : 'Recording saves the figures, the method and the value as a record that cannot change, under your name. It does not sign the report or produce the documents. A valuation signer can sign it later.')
            : delivered
              ? AMENDMENT_LINE
              : 'This saves your changes as a new record. The earlier record is kept in full; nothing is overwritten or deleted.'}
        </p>

        {changedSummary && (
          <p className="text-xs font-medium text-gray-700">{changedSummary}</p>
        )}

        <div>
          <p className="text-sm font-medium text-gray-900">Methodology reason</p>
          <p className="mb-3 whitespace-pre-wrap text-sm text-gray-700">
            {methodologyReason.trim() ? methodologyReason : 'None recorded'}
          </p>
          <label htmlFor="signoff-rationale" className="mb-1 block text-sm font-medium text-gray-900">
            Sign-off reason
          </label>
          <Textarea
            id="signoff-rationale"
            rows={4}
            value={rationale}
            onChange={e => onRationaleChange(e.target.value)}
            placeholder="Your reason for signing this record."
          />
          <p className="mt-1 text-xs text-gray-500">
            Frozen verbatim into the record and reproduced in the deliverable.
            {tuned && ' A tuned methodology cannot be signed without one.'}
          </p>
        </div>

        <Button
          className="w-full"
          disabled={!canFinalize || finalizing || noChangeMessage !== null}
          onClick={onFinalize}
        >
          {finalizeLabel(finalizing, liveVersion, willSign)}
        </Button>
        {(blockedReason || noChangeMessage) && (
          <p className="text-xs text-amber-800">{blockedReason ?? noChangeMessage}</p>
        )}
      </CardContent>
    </Card>
  )
}

/** VAL-FINALIZE-DONE-1: signed AND a document on file. Approved wording, used verbatim. */
export const DELIVERED_BAR_TEXT = 'Signed. The documents are ready.'
/** VAL-FINALIZE-STATE-2 (owner approved 2026-10-13): the bar's three signed states and the Produce reason. */
// WALKFIX-C AMEND_2 (owner 'Approve All' 2026-10-25 15:57): no version numbers. Arguments are kept for callers.
export const changedBarText = (_v?: number | null) => 'You changed inputs since the signed record. They are not signed yet.'
export const producedPendingBarText = (_v?: number | null) => 'Signed. Produce the documents.'
export const producedReasonText = (_v?: number | null) => 'Documents for this record are on file. Change an input to start a new record.'
export const START_NEW_VERSION = 'Start a new record'
export const AMENDMENT_LINE = 'A new record saves changes as an amendment. The signed report stays on file unchanged.'

/** FIX_VAL-SIGNOFF-MESSAGES-1 M2: files on file only count as delivered when the server says the
 *  live version can be produced (signer-signed) — an owner's paid Snapshot files do not. */
export function isDelivered(liveVersion: number | null, documentsOnFile: boolean, canProduce: boolean): boolean {
  return liveVersion !== null && documentsOnFile && canProduce
}

/** The one no-change wording — the Finalize card and the sticky Next step bar both show it. */
export function noChangeText(liveVersion: number | null, reissueChanged: boolean | null): string | null {
  return liveVersion !== null && reissueChanged === false
    ? 'Nothing has changed since the signed record. Change an input on Methodology to re-issue.'
    : null
}

/** The one wording of the finalize button — the sticky Next step bar reuses it. */
export function finalizeLabel(finalizing: boolean, liveVersion: number | null, willSign: boolean): string {
  if (finalizing) return 'Freezing…'
  if (liveVersion === null) return willSign ? 'Finalize and sign' : 'Record calculation'
  // WALKFIX-C AMEND_1 (owner ruling 2026-10-25): no version number. 'Record again' is FINAL (AMEND_2).
  return willSign ? 'Sign again' : 'Record again'
}

/** The finalize toast. 'Signed.' / 'Recorded.' are FINAL (AMEND_2; no version number). */
export function finalizedToastText(signed: boolean): string {
  return signed ? 'Signed.' : 'Recorded.'
}
